/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
import {
  useElement,
  useState,
  useStaleLayout,
  useRect,
  useEffect,
} from '@nebula.js/stardust';
import picassojs from 'picasso.js';
import picassoQ from 'picasso-plugin-q';
import picassoHammer from 'picasso-plugin-hammer';
import numeral from 'numeral';
import * as d3 from 'd3';
import slugify from 'utils/slugify';
import languagify from 'utils/languagify';

/*
* Total number of rows => 3873 - we remove 1 unknown (last segment) => 3872
* Desktop rows x columns => 121 x 32 | 6px x 6px | 1px gap
*
* to do:
* 3872 -> multidimensional array by segment
* for each array -> iterate through each item and render rect
* x and y offset for row/column - keep track of i - add gap
*
* x,y  x,y  x,y
* 0,0  1,0  2,0
* which row, which column, which segment

* x -> row * 2 + seg * 10
* y -> col * 2
*
* allow box size to change width % of width
* or control # of columns/row
*
* Responsive at 1100px width
*
*/

export default function supernova() {
  const picasso = picassojs();
  picasso.use(picassoQ);
  picasso.use(picassoHammer);

  // Custom component rect
  picasso.component('square', {
    require: ['renderer', 'resolver'],
    defaultSettings: {
      style: {},
    },
    render({ data }) {
      // resolve data/settings
      const { items } = this.resolver.resolve({
        data,
        settings: this.settings.settings,
      });

      // prettify data
      const _data = items.map((d) => (
        {
          dimension: d.data.label,
          colorby: d.data.colorBy.label,
          measure: d.data.measure.value,
          color: d.color,
        }
      ));

      // Helper function - Group Data of same Dimension (segment (mix))
      const groupBy = (key) => (array) => array.reduce((objectsByKeyValue, obj) => {
        const value = obj[key];
        objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
        return objectsByKeyValue;
      }, {});

      // Group data by segment (1st dimension)
      const groupedData = groupBy('dimension')(_data);

      // Move it to an array
      const sortableArray = [];
      Object.keys(groupedData).forEach((seg) => {
        let sum = 0;
        groupedData[seg].forEach((d) => {
          sum += d.measure;
        });
        sortableArray.push([seg, sum, groupedData[seg]]);
      });

      // Sort dimensions
      const sortedArray = sortableArray.sort(((index) => (a, b) => {
        if (a[0] === 'Unknown' || b[0] === 'Unknown') return 1;
        return (a[index] === b[index] ? 0 : (a[index] > b[index] ? -1 : 1));
      })(1));

      // Grid Sizing
      let total = 0;
      total = d3.sum(_data, (d) => d.measure);
      const numOfSections = Object.values(_data.reduce((acc, curr) => {
        if (!acc[curr.dimension]) acc[curr.dimension] = [];
        acc[curr.dimension].push(curr);
        return acc;
      }, {})).length;
      const squareSize = this.rect.width >= 768 ? 6 : 4;
      const gap = this.rect.width >= 768 ? 2 : 1;
      const heightSquares = Math.ceil(total / (Math.floor(this.rect.width / (squareSize + gap)) - numOfSections));
      const height = heightSquares * (squareSize + gap);
      document.querySelector('#waffleViz').style.height = `${height}px`;
      let theData = [];
      // remap data, array with all square items
      sortedArray.forEach((arr, i) => {
        // Sort units
        arr[2].sort((a, b) => {
          if (a.colorby === 'Unknown' || b.colorby === 'Unknown') return 1;
          const aVal = Math.floor(a.measure);
          const bVal = Math.floor(b.measure);
          return aVal > bVal ? -1 : 1;
        });
        // Build Matrix
        arr[2].forEach((d) => {
          d.units = Math.floor(d.measure);
          theData = theData.concat(
            Array(d.units + 1).join('X').split('').map(() => ({
              color: d.color,
              group: d.dimension,
              colorBy: d.colorby,
              groupIndex: i,
            })),
          );
        });
      });

      // Render Rect Components
      let overflow = 0;
      const rectComponents = theData.reduce((acc, curr, i) => {
        if (theData[i - 1] && theData[i].groupIndex !== theData[i - 1].groupIndex) {
          overflow += (i - overflow) % heightSquares;
        }
        const seg = curr.groupIndex;
        const col = Math.floor((i - overflow) / heightSquares);
        const row = (i - overflow) % heightSquares;

        return [
          ...acc,
          {
            type: 'rect',
            x: ((col * squareSize) + (col * gap)) + (seg * (squareSize + gap) * 2),
            y: (row * squareSize) + (row * gap),
            width: squareSize,
            height: squareSize,
            fill: curr.group === 'Unknown' ? '#BFBFBF50' : curr.color,
          },
        ];
      }, []);

      let endCol = 0;

      const containerComponents = sortedArray.map((segment, i) => {
        const tooltipData = {};
        const startCol = endCol;
        let numofUnits = 0;

        if (segment[2].length === 1) {
          numofUnits = segment[2][0].units;
          tooltipData[slugify(segment[0])] = { perc: segment[1] / total, color: segment[2][0].color };
        } else {
          segment[2].forEach((s) => {
            numofUnits += s.units;
            // calculate tooltip data to pass
            tooltipData[slugify(s.colorby)] = { perc: s.measure / segment[1], color: s.color };
          });
        }
        const numofCols = Math.floor(numofUnits / heightSquares) + 1;
        endCol += numofCols - 1;

        return {
          type: 'rect',
          x: ((startCol * squareSize) + (startCol * gap)) + (i * (squareSize + gap) * 2),
          y: 0,
          width: ((numofCols * squareSize) + (numofCols * gap)),
          height: heightSquares * (squareSize + gap),
          fill: 'none',
          tag: 'seg',
          data: tooltipData,
        };
      });

      let endColLabel = 0;
      const labelComponents = sortedArray.map((segment, i) => {
        const startCol = endColLabel;
        let numofUnits = 0;
        if (segment[2].length === 1) {
          numofUnits = segment[2][0].units;
        } else {
          segment[2].forEach((s) => { numofUnits += s.units; });
        }
        const numofCols = Math.floor(numofUnits / heightSquares) + 1;
        endColLabel += numofCols - 1;
        const segWidth = ((numofCols * squareSize) + (numofCols * gap));

        return {
          type: 'text',
          text: `${numeral((segment[1] / total)).format('0%')} ${languagify(segment[0]).toUpperCase()}`,
          x: ((startCol * squareSize) + (startCol * gap)) + (i * (squareSize + gap) * 2) + (segWidth / 2),
          y: heightSquares * (squareSize + gap),
          transform: `rotate(220, ${((startCol * squareSize) + (startCol * gap)) + (i * (squareSize + gap) * 2) + (segWidth / 2)}, ${heightSquares * (squareSize + gap)} )`,
          transformOrigin: 'center',
          fontSize: this.rect.width >= 768 ? 12 : 10,
        };
      });

      return [...rectComponents, ...containerComponents, ...labelComponents];
    },
  });

  return {
    qae: {
      properties: {
        qHyperCubeDef: {
          qDimensions: [],
          qMeasures: [],
          qInitialDataFetch: [{ qWidth: 3, qHeight: 3300 }],
          qSuppressZero: false,
          qSuppressMissing: true,
        },
        showTitles: true,
        title: '',
        subtitle: '',
        footnote: '',
      },
      data: {
        targets: [
          {
            path: '/qHyperCubeDef',
            dimensions: {
              min: 1,
              max: 2,
            },
            measures: {
              min: 1,
              max: 1,
            },
          },
        ],
      },
    },
    component() {
      const element = useElement();
      const layout = useStaleLayout();
      const rect = useRect();
      const [instance, setInstance] = useState();

      useEffect(() => {
        const p = picasso.chart({
          element,
          data: [],
          settings: {},
        });

        setInstance(p);

        return () => {
          p.destroy();
        };
      }, []);

      useEffect(() => {
        if (!instance) {
          return;
        }

        const colorScale = d3.scaleOrdinal()
          .domain(['Men', 'Women', 'Non-Binary', 'Asian', 'Black', 'Indigenous', 'Latin', 'Middle Eastern', 'Mixed Race', 'Unknown', 'White', 'Full-time', 'Interns', 'Part-time', 'Supervisor'])
          .range(['#9EBFCE', '#BE7999', '#204635', '#C17A82', '#A55886', '#408CA0', '#A2C3B4', '#E8CEAA', '#4CDBD7', '#BFBFBF50', '#A7A4D4', '#24796C', '#B890D3', '#2F8AC4', '#CDB3A2']);

        instance.update({
          data: [
            {
              type: 'q',
              key: 'qHyperCube',
              data: layout.qHyperCube,
            },
          ],
          settings: {
            scales: {},
            components: [
              {
                type: 'square',
                displayOrder: 3,
                data: {
                  extract: {
                    field: 'qDimensionInfo/0',
                    props: {
                      measure: {
                        field: 'qMeasureInfo/0',
                      },
                      colorBy: {
                        field: 'qDimensionInfo/1',
                      },

                    },
                  },
                },
                settings: {
                  color: (d) => colorScale(d.datum.colorBy.label),
                },
              },
              {
                key: 'tooltip',
                type: 'tooltip',
                displayOrder: 10,
                settings: {
                  duration: 15000,
                  // Target point marker
                  filter: (nodes) => nodes.filter((node) => node.tag === 'seg' && Object.keys(node.data).length !== 0),
                  // Extract data
                  extract: ({ node }) => node.data,
                  content: ({ h, data }) => { // eslint-disable-line no-shadow
                    const els = [];
                    data.forEach((node) => {
                      Object.keys(node).forEach((d) => els.push(
                        h('tr', { style: { padding: 4, fontWeight: 600 } },
                          [h('td', { style: { padding: 4, fontWeight: 600, textTransform: 'capitalize' } }, [
                            h('div', {
                              style: {
                                display: 'inline-block', marginRight: 3, width: 8, height: 8, backgroundColor: node[d].color,
                              },
                            }, ''),
                            languagify(`${d.replace(/\s+/g, '-')}:`)]),
                          h('td', { style: { padding: 4 } }, numeral(node[d].perc).format('0.0%')),
                          ]),
                      ));
                    });
                    return h('div', { style: { padding: 10 } }, els);
                  },
                  placement: {
                    type: 'pointer',
                    area: 'target',
                    dock: 'auto',
                  },
                },
              },
            ],
            interactions: [
              {
                type: 'native',
                events: {
                  mousemove(e) {
                    this.chart.component('tooltip').emit('show', e);
                  },
                  mouseleave() {
                    this.chart.component('tooltip').emit('hide');
                  },
                },
              },
              {
                type: 'hammer',
                gestures: [
                  {
                    type: 'Tap',
                    events: {
                      tap(e) {
                        this.chart.component('tooltip').emit('show', e);
                      },
                    },
                  },
                ],
              },
            ],
          },
        });
      }, [layout, instance]);

      useEffect(() => {
        if (!instance) {
          return;
        }
        instance.update();
      }, [rect.width, rect.height, instance]);
    },
  };
}
